/**
 * Everything the Settings page knows about connected ad-platform accounts:
 * loading them, connecting, reconnecting, disconnecting, syncing, testing,
 * editing and backfilling — plus the derived facts the page renders.
 *
 * Lifted wholesale out of SettingsPage, which was 1,053 lines of which ~550
 * were this. The move is deliberately ALL-OR-NOTHING: every piece of state and
 * every effect comes across together and the hook is called once, so React sees
 * exactly the same hook order it saw before and no effect changes its
 * dependencies or its position relative to the others.
 *
 * The 39-name return is not a design flourish — it is the honest width of what
 * one page was doing in one function. Splitting it further is a question about
 * the PAGE, and belongs to whoever decides the page should do less.
 */

import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { daysBetween } from "@/components/settings/connectionSummary";
import { todayDataDate } from "@/services/dataDate";
import type { PlatformConfig, PlatformAccount, PlatformType } from "@/types";
import {
  getConnectedAccounts,
  connectPlatform,
  savePlatformSecret,
  updatePlatformAccount,
  disconnectPlatform,
  triggerSync,
  getSyncStatus,
  fetchPlatformFrontiers,
} from "@/services/platformAccountService";
import { checkAccountHealth, isHealthTestable, type AccountHealth } from "@/services/accountHealth";
import { fetchEmptySyncReasons } from "@/services/lastSyncOutcome";
import { logError } from "@/services/logger";
import { requestBackfill, defaultBackfillRange } from "@/services/backfillService";
import type { BackfillBatchOutcome, BackfillChunk, BackfillRequest } from "@/services/backfillService";
import { useCanAccess } from "@/hooks/useCanAccess";
import { DEMO_MODE } from "@/config/demoConfig";
import { getDemoPlatformAccounts } from "@/services/demoDataService";
import { userFacingMessage } from "@/services/codedError";
import { AVAILABLE_PLATFORMS } from "@/components/settings/availablePlatforms";

export function usePlatformAccounts() {
  const { workspace } = useWorkspace();
  const { t } = useTranslation();

  const [accounts, setAccounts] = useState<PlatformAccount[]>([]);
  // DATA frontier ต่อ platform (ข้อมูลถึงวันไหน) + วันแรกสุดบนระบบ — โหลดคู่กับ
  // accounts และรีเฟรชด้วยกันหลัง sync จบ (loadAccounts ถูกเรียกซ้ำที่นั่นอยู่แล้ว)
  const [frontiers, setFrontiers] = useState<Map<PlatformType, string>>(new Map());
  // Platforms whose frontier lookup failed: absence from `frontiers` alone
  // cannot tell "never delivered" from "we could not find out".
  const [unreadableFrontiers, setUnreadableFrontiers] = useState<Set<PlatformType>>(new Set());
  const [firstDataDate, setFirstDataDate] = useState<string | null>(null);
  // accountId -> why that account's last FINISHED sync wrote nothing. Loaded
  // with the accounts rather than polled: the question "sync แล้วไม่มีข้อมูล"
  // gets asked on a cold page load, long after the run it is about.
  const [emptySyncReasons, setEmptySyncReasons] = useState<ReadonlyMap<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncingAccounts, setSyncingAccounts] = useState<string[]>([]);
  // Enqueued but unclaimed. Separate from syncingAccounts so the button can say
  // which of the two is happening instead of calling both "Syncing".
  const [queuedAccounts, setQueuedAccounts] = useState<string[]>([]);
  // Live "Test connection" state — ephemeral (cleared on reload) by design.
  // Only the MESSAGE is: a passing test also writes last_verified_at on the
  // account row, server-side, and that survives the reload in account.status.
  const [testingAccounts, setTestingAccounts] = useState<string[]>([]);
  const [accountHealth, setAccountHealth] = useState<Record<string, AccountHealth>>({});
  // The account currently open in the Edit modal (null = closed).
  const [editingAccount, setEditingAccount] = useState<PlatformAccount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * workspace ที่ pin ไว้ตอนเปิดโมดัล
   *
   * ห้ามเปลี่ยนกลับไปอ่าน `workspace?.id` สดตอน insert: OAuth เป็น popup หน้านี้จึง
   * ไม่ unmount และ sidebar switcher ยังกดได้ระหว่างโมดัลเปิดอยู่ ถ้าอ่านค่าสด
   * การสลับ workspace กลางคันจะพา account ไปลงที่ใหม่โดยไม่มีสัญญาณอะไรเลย
   */
  interface PendingConnect {
    platform: PlatformConfig;
    workspaceId: string;
    workspaceName: string;
    /** ตั้งเมื่อเป็น Reconnect ของแถวเดิม — ไม่ตั้งแปลว่าเป็นการต่อ account ใหม่ */
    reconnect?: { accountId: string; externalAccountId: string };
  }

  const [pendingConnect, setPendingConnect] = useState<PendingConnect | null>(null);

  /**
   * Load platform accounts.
   *
   * `silent` skips the page spinner. The Settings integrations tab swaps the
   * whole account list for a Loader2 on isLoading, so an unannounced re-read —
   * the one that follows a passing "Test connection" — would blank the list the
   * reader is looking at, for a round trip they did not ask to wait for. The
   * first load, and the reload after a sync, still announce themselves: there
   * the wait is the point.
   */
  const loadAccounts = useCallback(async (options?: { silent?: boolean }) => {
    if (!workspace?.id) {
      setIsLoading(false);
      return;
    }

    if (!options?.silent) setIsLoading(true);
    setError(null);

    try {
      // Get connected accounts from database. Frontier ล้มเหลวไม่ควรพังทั้งหน้า
      // — จับแยกแล้วปล่อยเป็น Map ว่าง (แถวจะขึ้น "ยังไม่มีข้อมูล" ตามจริง)
      const [data, frontierResult] = await Promise.all([
        getConnectedAccounts(workspace.id),
        fetchPlatformFrontiers(
          workspace.id,
          AVAILABLE_PLATFORMS.map((p) => p.type)
        ).catch(() => ({
          frontiers: new Map<PlatformType, string>(),
          // The whole lookup failed, so nothing is known about any platform.
          unreadable: new Set<PlatformType>(AVAILABLE_PLATFORMS.map((p) => p.type)),
          firstDate: null,
        })),
      ]);
      setFrontiers(frontierResult.frontiers);
      setUnreadableFrontiers(frontierResult.unreadable);
      setFirstDataDate(frontierResult.firstDate);

      // Sequential rather than in the Promise.all above, because this read is
      // keyed by account id and the ids are what the call above just returned.
      // Awaited rather than fired off, so the list and the explanations beside
      // it appear together and no setState can land after an unmount; it never
      // throws (fetchEmptySyncReasons logs a failed lookup and omits the
      // account), so a missing explanation cannot cost the reader the list.
      setEmptySyncReasons(await fetchEmptySyncReasons(data.map((a) => a.id)));

      // In demo mode, also show demo platform accounts
      if (DEMO_MODE) {
        const demoAccounts = getDemoPlatformAccounts();
        // Filter out demo accounts that already exist in real data (by platform)
        const existingPlatforms = new Set(data.map((acc) => acc.platform));
        const additionalDemoAccounts = demoAccounts.filter(
          (acc) => !existingPlatforms.has(acc.platform as PlatformType)
        );
        setAccounts([...data, ...additionalDemoAccounts] as PlatformAccount[]);
      } else {
        setAccounts(data);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("settings.errLoadAccounts");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspace?.id, t]);

  // Handle connect platform
  const handleConnect = useCallback(
    (platform: PlatformConfig) => {
      if (!workspace?.id) return;
      setError(null);
      setPendingConnect({
        platform,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
      });
      setIsModalOpen(true);
    },
    [workspace?.id, workspace?.name]
  );

  // Reconnect = re-auth ของแถวเดิม ใช้โมดัลและ OAuth flow ชุดเดียวกับการต่อใหม่
  // ต่างกันแค่ปลายทาง: update แถวเดิม ไม่ใช่ insert แถวใหม่ (ดู handleModalConnect)
  //
  // ต้อง update ไม่ใช่ insert เพราะ media_campaigns.account_id เป็น ON DELETE SET
  // NULL (20240101000002_media_schema.sql:46) — ถ้าให้คนแก้ token หมดอายุด้วยการ
  // Disconnect แล้วต่อใหม่ campaign เดิมจะขาดจาก account ถาวร
  const handleReconnect = useCallback(
    (account: PlatformAccount) => {
      if (!workspace?.id) return;
      const platform = AVAILABLE_PLATFORMS.find((p) => p.type === account.platform);
      if (!platform) return;
      setError(null);
      setPendingConnect({
        platform,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        reconnect: { accountId: account.id, externalAccountId: account.accountId },
      });
      setIsModalOpen(true);
    },
    [workspace?.id, workspace?.name]
  );

  // Handle disconnect platform
  const handleDisconnect = useCallback(
    async (accountId: string) => {
      if (!confirm(t("settings.confirmDisconnect"))) {
        return;
      }

      try {
        await disconnectPlatform(accountId);
        await loadAccounts();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t("settings.errDisconnect");
        setError(errorMessage);
      }
    },
    [loadAccounts, t]
  );

  // Handle sync
  const handleSync = useCallback(
    async (accountId: string) => {
      setError(null);
      // Optimistically pending. triggerSync only ENQUEUES a job, so the pill must
      // stay on until the background worker finishes — the poller below tracks it
      // via getSyncStatus and clears it on success/failure.
      //
      // Marked queued as well as pending: what just happened is an enqueue, and
      // the first poll is four seconds away, so without this the button reads
      // "Syncing…" for four seconds before correcting itself to "Queued…".
      setSyncingAccounts((prev) => (prev.includes(accountId) ? prev : [...prev, accountId]));
      setQueuedAccounts((prev) => (prev.includes(accountId) ? prev : [...prev, accountId]));

      try {
        const result = await triggerSync(accountId, t);
        if (!result.success) {
          setSyncingAccounts((prev) => prev.filter((id) => id !== accountId));
          setQueuedAccounts((prev) => prev.filter((id) => id !== accountId));
          setError(result.message);
        }
      } catch (err) {
        setSyncingAccounts((prev) => prev.filter((id) => id !== accountId));
        setQueuedAccounts((prev) => prev.filter((id) => id !== accountId));
        setError(err instanceof Error ? err.message : t("settings.errTriggerSync"));
      }
    },
    [t]
  );

  // Poll real sync status for accounts the worker is processing. Because triggerSync
  // only enqueues (returning instantly), the "syncing" pill would otherwise flash off
  // while the job runs server-side for ~1-2 min. Poll getSyncStatus (reads sync_jobs)
  // until the job is done, then refresh the account data and surface any error.
  useEffect(() => {
    if (syncingAccounts.length === 0) return;
    let cancelled = false;
    const startedAt = Date.now();
    const MAX_POLL_MS = 8 * 60 * 1000; // stop the spinner if no worker ever picks it up

    const poll = async () => {
      const results = await Promise.all(
        syncingAccounts.map(async (id) => {
          try {
            return { id, status: await getSyncStatus(id, t) };
          } catch {
            return { id, status: null };
          }
        })
      );
      if (cancelled) return;

      setQueuedAccounts(results.filter((r) => r.status?.isQueued).map((r) => r.id));

      const finished = results.filter((r) => r.status && !r.status.isSyncing);
      // A job nobody has claimed is not progress. Previously it kept the spinner
      // for the full eight minutes and then stopped with no explanation, which
      // reads as "finished" — the one thing it did not do.
      const stalled = results.filter((r) => r.status?.isStalled);
      const timedOut = Date.now() - startedAt > MAX_POLL_MS ? syncingAccounts : [];
      const toClear = new Set<string>([
        ...finished.map((r) => r.id),
        ...stalled.map((r) => r.id),
        ...timedOut,
      ]);
      if (toClear.size === 0) return;

      setSyncingAccounts((prev) => prev.filter((id) => !toClear.has(id)));

      // Reported AFTER the reload, not before: loadAccounts opens with
      // setError(null), so a message set here was wiped by the await on the next
      // line and never reached the screen. That silently swallowed sync failures
      // too, not only the queue notice added here.
      await loadAccounts();

      const failed = finished.find((r) => r.status?.error);
      if (failed?.status?.error) setError(t("settings.errSyncFailed", { reason: failed.status.error }));
      else if (stalled.length > 0 || timedOut.length > 0) {
        // Two different failures with two different remedies: nobody ever
        // started the job, versus somebody started it and stopped.
        const abandoned = stalled.some((r) => r.status?.stalledReason === "abandoned");
        setError(
          abandoned
            ? t("settings.errSyncAbandoned")
            : t("settings.errSyncNoWorker"),
        );
      }
    };

    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [syncingAccounts, loadAccounts, t]);

  /**
   * Live "Test connection" — verify a stored credential against the platform now.
   *
   * checkAccountHealth never throws (it resolves to a failed/unsupported
   * result), so the spinner is always cleared.
   *
   * The MESSAGE stays in state, as it always did: "Ready — Brand A" is about
   * this click and belongs to this screen. What no longer stops at state is the
   * FACT that the credential worked. /api/account-health writes
   * last_verified_at on the account row, with the service role, and
   * derivePassiveStatus reads it — so the re-read below is how a row that said
   * "ยังไม่ได้ตรวจสอบ" stops saying it. Before this, handleTest called
   * setAccountHealth and nothing else: the owner tested an account, Facebook
   * said it was fine, and the row ignored the answer and kept asking to be
   * tested, because a sync was the only thing that could clear it and that
   * account had never synced.
   *
   * Only a PASS triggers the re-read. A failure and an unsupported platform
   * write nothing server-side, so there is nothing new to read, and a reload
   * would spend a round trip to render the row exactly as it already is.
   */
  const handleTest = useCallback(async (account: PlatformAccount) => {
    setError(null);
    setTestingAccounts((prev) => (prev.includes(account.id) ? prev : [...prev, account.id]));
    const result = await checkAccountHealth(account, t);
    setAccountHealth((prev) => ({ ...prev, [account.id]: result }));
    setTestingAccounts((prev) => prev.filter((id) => id !== account.id));
    if (result.state === "ready") await loadAccounts({ silent: true });
  }, [t, loadAccounts]);

  const handleTestAll = useCallback(async () => {
    const testable = accounts.filter(isHealthTestable);
    if (testable.length === 0) return;
    setError(null);
    setTestingAccounts(testable.map((a) => a.id));
    const results = await Promise.all(
      testable.map(async (a) => [a.id, await checkAccountHealth(a, t)] as const),
    );
    setAccountHealth((prev) => {
      const next = { ...prev };
      for (const [id, r] of results) next[id] = r;
      return next;
    });
    setTestingAccounts([]);
    // ONE re-read for the whole batch, not one per account: every stamp is
    // already written by the time the last promise settles, and loadAccounts
    // runs the per-platform frontier queries too — six of them, for an answer
    // that does not differ per row.
    if (results.some(([, r]) => r.state === "ready")) await loadAccounts({ silent: true });
  }, [accounts, t, loadAccounts]);

  // Sync every real account at once. Same enqueue+poll model as handleSync: each
  // triggerSync only queues a job; the poll effect above tracks completion and
  // clears the pills. Demo accounts have no DB row, so they're skipped.
  const handleSyncAll = useCallback(async () => {
    const real = accounts.filter((a) => a.accountType !== "demo_data");
    if (real.length === 0) return;
    setError(null);
    // Queued as well as pending — see handleSync: the first poll is four seconds
    // out, and until then the button would call an enqueue "Syncing…".
    setSyncingAccounts((prev) => Array.from(new Set([...prev, ...real.map((a) => a.id)])));
    setQueuedAccounts((prev) => Array.from(new Set([...prev, ...real.map((a) => a.id)])));

    const results = await Promise.all(
      real.map(async (a) => {
        try {
          const r = await triggerSync(a.id, t);
          return { id: a.id, ok: r.success, message: r.message };
        } catch (err) {
          return { id: a.id, ok: false, message: err instanceof Error ? err.message : "Failed to trigger sync" };
        }
      }),
    );

    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      const failedIds = new Set(failed.map((f) => f.id));
      setSyncingAccounts((prev) => prev.filter((id) => !failedIds.has(id)));
      setQueuedAccounts((prev) => prev.filter((id) => !failedIds.has(id)));
      setError(t("settings.errQueueSome", { count: failed.length, reason: failed[0].message }));
    }
  }, [accounts, t]);

  // Edit an account's name / id. handleSaveEdit intentionally does NOT catch:
  // EditAccountModal awaits it and renders any thrown message inline (e.g. the
  // duplicate-id 23505 message), keeping the modal open so the user can fix it.
  const handleEdit = useCallback((account: PlatformAccount) => {
    setEditingAccount(account);
  }, []);

  const handleSaveEdit = useCallback(
    async (updates: { accountName: string; accountId: string }) => {
      if (!editingAccount) return;
      await updatePlatformAccount(editingAccount.id, {
        account_name: updates.accountName,
        external_account_id: updates.accountId,
      });
      await loadAccounts();
    },
    [editingAccount, loadAccounts],
  );

  // Handle modal connect
  const handleModalConnect = useCallback(
    async (credentials: {
      access_token: string;
      refresh_token?: string;
      account_id: string;
      account_name?: string;
      expires_at?: string;
      method?: "token" | "oauth2" | "service_account";
      // Service-account only: goes to the service_role-only secrets table, never
      // onto the browser-readable media_platform_accounts row.
      service_account?: {
        service_account_json: string;
        impersonated_user: string;
        developer_token: string;
        login_customer_id?: string;
      };
    }) => {
      if (!pendingConnect) return;
      const { platform, workspaceId, workspaceName, reconnect } = pendingConnect;

      // workspace ถูกสลับระหว่างโมดัลเปิดอยู่ → ยกเลิก ไม่เขียนที่ไหนทั้งนั้น
      // เขียนลง workspace ที่เริ่มก็ได้ แต่ผู้ใช้กำลังมองอีก workspace หนึ่งอยู่และ
      // จะไม่เห็นแถวใหม่ — เงียบพอ ๆ กับเขียนผิดที่
      if (workspace?.id !== workspaceId) {
        setIsModalOpen(false);
        setPendingConnect(null);
        setError(
          t("settings.errSwitchedAway", { workspace: workspaceName })
        );
        return;
      }

      // Reconnect ที่ไปจบด้วย account คนละตัวกับแถวที่กด ไม่ใช่ reconnect แล้ว —
      // การเขียน token ของ account B ทับแถวของ account A ทำให้แถวนั้นโกหกว่าตัวเอง
      // เป็นใคร และ metric ที่ sync มาหลังจากนั้นจะถูกโยนเข้า campaign ของ A
      if (reconnect && credentials.account_id !== reconnect.externalAccountId) {
        // ไม่ปิดโมดัล ไม่ล้าง pendingConnect: ต่างจาก guard เรื่องสลับ workspace
        // ด้านบน ที่นี่ผู้ใช้แก้ไขได้ในโมดัลเดิม (เลือก account ให้ตรง) — throw ให้
        // ตรงกับ contract ของ onConnect: โมดัลจริง await อยู่ใน try/catch แล้วโชว์
        // error inline โดยไม่ปิดตัวเอง (ConnectPlatformModal.tsx:290-318) การ return
        // เฉย ๆ จะทำให้ promise resolve และโมดัลคิดว่าสำเร็จ
        const message =
          t("settings.differentAccount");
        setError(message);
        throw new Error(message);
      }

      // spec §3.5 — DB ไม่มี unique constraint บน
      // (workspace_id, platform, external_account_id) แถวซ้ำจึงสร้างได้ และผลคือทุก
      // dashboard นับข้อมูลของ account นั้นสองรอบ ตัวกันจริงต้องอยู่ที่ DB (spec §4)
      // นี่คือด่านหน้าที่ UI ทำได้
      //
      // ข้ามด่านนี้ตอน reconnect: accountId เดิมย่อมมีอยู่แล้วเป็นธรรมดา ถ้าไม่ข้าม
      // Reconnect จะโดนด่านนี้บล็อกทุกครั้งและใช้ไม่ได้เลย
      const alreadyConnected =
        !reconnect &&
        accounts.some(
          (account) =>
            account.platform === platform.type && account.accountId === credentials.account_id
        );
      if (alreadyConnected) {
        // เหตุผลเดียวกับ guard ด้านบน: ไม่ปิดโมดัล ปล่อยให้ผู้ใช้แก้ account id ในที่
        // เดิมได้ — throw เพื่อให้ promise reject ตาม contract ที่โมดัลจริงคาดหวัง
        const message = t('settings.duplicateAccount');
        setError(message);
        throw new Error(message);
      }

      setIsConnecting(true);
      setError(null);

      try {
        let expiresAt = credentials.expires_at;
        // OAuth tokens expire; if the exchange didn't return an expiry, default one.
        // Token/service-account methods don't: SA mints a fresh token per sync job.
        //
        // Both branches are SECONDS. The Facebook branch used to read 60 * 24 —
        // written as if it meant days or hours, but multiplied by 1000 as
        // seconds, so it stamped a long-lived token as expiring in 24 MINUTES.
        // The Facebook OAuth path stores no refresh token, so derivePassiveStatus
        // then flipped a working connection to "Token expired — reconnect".
        if (!expiresAt && credentials.method === "oauth2") {
          const SECONDS_PER_DAY = 24 * 60 * 60;
          const expiresInSeconds =
            platform.type === "facebook"
              ? 60 * SECONDS_PER_DAY // Facebook long-lived user token: 60 days
              : 60 * 60; // Google/others access token: 1 hour
          expiresAt = new Date(Date.now() + expiresInSeconds * 1000).toISOString();
        }

        // Server-only secret bundle (private key + developer token), if any.
        const secretConfig = credentials.service_account
          ? {
              auth_method: "service_account",
              service_account_json: credentials.service_account.service_account_json,
              impersonated_user: credentials.service_account.impersonated_user,
              developer_token: credentials.service_account.developer_token,
              login_customer_id: credentials.service_account.login_customer_id,
            }
          : null;

        if (reconnect) {
          // update แถวเดิม ไม่ใช่ insert — campaign ที่ชี้มาที่ account นี้จึงยังชี้อยู่
          // `sync_error: ""` เพื่อล้างสถานะ error: toPlatformAccount อ่าน sync_error
          // แบบ truthy (platformAccountService.ts:46) สตริงว่างจึงกลับไป active
          await updatePlatformAccount(reconnect.accountId, {
            access_token: credentials.access_token,
            refresh_token: credentials.refresh_token,
            token_expires_at: expiresAt,
            sync_error: "",
            ...(credentials.method ? { connection_method: credentials.method } : {}),
          });
          if (secretConfig) await savePlatformSecret(reconnect.accountId, workspaceId, secretConfig);
        } else {
          const account = await connectPlatform(workspaceId, platform.type, {
            ...credentials,
            expires_at: expiresAt,
          });
          if (secretConfig) {
            try {
              await savePlatformSecret(account.id, workspaceId, secretConfig);
            } catch (secretErr) {
              // Roll back the account row so a failed secret-save doesn't leave an
              // orphan — an orphan has no credentials (can't sync) yet blocks
              // reconnecting via the unique constraint. Keep connect atomic.
              await disconnectPlatform(account.id).catch(() => {});
              throw secretErr;
            }
          }

          // A year of history, requested once, here.
          //
          // Routine syncing reaches back seven days, so without this the account
          // just connected would show a week and nothing behind it — for as long
          // as it exists, because nothing else ever asks for anything older. On
          // one audited account that left 41% of the real Facebook spend in the
          // database and five whole months absent.
          //
          // On the NEW-account branch only: a reconnect is re-authenticating a
          // row that already has its history, and re-fetching a year of it would
          // spend a large amount of somebody's API budget to rewrite rows that
          // are already correct.
          //
          // Deliberately not fatal. The account is connected and usable; a
          // history request that could not be queued is a thing to retry from
          // the panel in this tab, not a reason to fail the connect and roll
          // back credentials that worked.
          try {
            const { queued, alreadyQueued } = await requestBackfill(account.id, defaultBackfillRange(), t);
            // Both: the notice says how much history is on its way, and a panel
            // click that got there first leaves this call with nothing to add.
            setBackfillNotice(t("backfill.autoQueued", { count: queued + alreadyQueued }));
          } catch (backfillErr) {
            logError("Settings", "Could not queue connect-time backfill", backfillErr);
            setBackfillNotice(t("backfill.autoFailed"));
          }
        }

        setIsModalOpen(false);
        setPendingConnect(null);
        await loadAccounts();
      } catch (err) {
        // Coded errors are sentences written for a reader, so they come back
        // translated. A diagnostic keeps its own text: "Failed to connect
        // platform: <db detail>" has no useful translation, and replacing it
        // with a vague translated one would cost the detail that makes it
        // actionable.
        setError(userFacingMessage(err, t, t("settings.connectFailed")));
        throw err;
      } finally {
        setIsConnecting(false);
      }
    },
    [pendingConnect, workspace?.id, accounts, loadAccounts, t]
  );

  // Handle modal close
  const handleModalClose = useCallback(() => {
    if (!isConnecting) {
      setIsModalOpen(false);
      setPendingConnect(null);
    }
  }, [isConnecting]);

  // Load accounts on mount and when workspace changes
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // ---- hero-level derived state --------------------------------------------
  // "today" on the DATA calendar (Asia/Bangkok, dataDate.ts) — NOT the browser
  // clock. Frontier labels and the strip both judge against this.
  const todayIso = todayDataDate();
  const realAccounts = accounts.filter((a) => a.accountType !== "demo_data");

  // admin OR analyst, matching what the RLS on both sync_jobs and
  // workspace_outcomes actually permits — their insert policies name both
  // roles, so gating on admin alone would refuse a write the database was
  // prepared to accept. One flag, because it is one question: may this person
  // put data into this workspace.
  const canWriteData = useCanAccess(["admin", "analyst"]);

  // Said out loud rather than left to the sync pills: connecting an account now
  // queues a year of history, which is a dozen background jobs and minutes of
  // work. A person who is not told assumes the blank figures mean it failed.
  const [backfillNotice, setBackfillNotice] = useState<string | null>(null);

  /**
   * Queue a backfill for every connected account.
   *
   * One range, applied across the accounts, because the question a person is
   * asking is "how far back does this workspace go" rather than one about a
   * single platform. Failures are per-account: one platform refusing must not
   * discard the requests that were accepted for the others.
   */
  const handleBackfill = useCallback(
    async (accountIds: string[], range: BackfillChunk): Promise<BackfillBatchOutcome> => {
      const results = await Promise.allSettled(
        accountIds.map((id) => requestBackfill(id, range, t)),
      );
      const sum = (pick: (r: BackfillRequest) => number): number =>
        results.reduce((total, r) => total + (r.status === "fulfilled" ? pick(r.value) : 0), 0);
      const queued = sum((r) => r.queued);
      // Counted from what each account reported, not from the panel's total minus
      // `queued`: an account that failed also leaves `queued` short of the total.
      const alreadyQueued = sum((r) => r.alreadyQueued);
      const toError = (reason: unknown): Error => (reason instanceof Error ? reason : new Error(String(reason)));
      // Results come back in the order the accounts went in, so each failure
      // keeps the account it belongs to.
      const failed = results.flatMap((r, i) =>
        r.status === "rejected" ? [{ accountId: accountIds[i], error: toError(r.reason) }] : [],
      );
      // Nothing queued at all is a failure worth raising; a partial success is
      // reported as the count, because those jobs are genuinely running.
      if (queued === 0 && failed.length > 0) throw failed[0].error;
      // And the failures travel with it. Dropped here, the panel printed a green
      // "Queued 13 requests" under a cost line that said 26 and nothing else.
      return {
        queued,
        alreadyQueued,
        failures: failed.map((f) => ({ accountId: f.accountId, reason: f.error.message })),
      };
    },
    [t],
  );
  const connectedCount = realAccounts.filter((a) => a.status !== "disconnected").length;
  // ISO strings sort lexicographically = chronologically
  const latestSyncAt = realAccounts
    .map((a) => a.lastSyncAt)
    .filter((d): d is string => Boolean(d))
    .sort()
    .at(-1);

  const anyTestable = accounts.some(isHealthTestable);
  const isTestingAny = testingAccounts.length > 0;
  const anySyncable = realAccounts.length > 0;
  const isSyncingAny = syncingAccounts.length > 0;
  // Every pending account is still sitting in the queue, so nothing is actually
  // running yet. Saying "Syncing" there describes work that has not started.
  const allQueued = isSyncingAny && syncingAccounts.every((id) => queuedAccounts.includes(id));

  // Workspace tab facts — ช่วงข้อมูลบนระบบ นับจากวันแรกสุดถึง frontier ล่าสุด
  const latestFrontier = [...frontiers.values()].sort().at(-1) ?? null;
  const historyDays =
    firstDataDate && latestFrontier ? daysBetween(firstDataDate, latestFrontier) + 1 : null;

  return {
    accountHealth,
    accounts,
    allQueued,
    anySyncable,
    anyTestable,
    backfillNotice,
    canWriteData,
    connectedCount,
    editingAccount,
    emptySyncReasons,
    error,
    frontiers,
    handleBackfill,
    handleConnect,
    handleDisconnect,
    handleEdit,
    handleModalClose,
    handleModalConnect,
    handleReconnect,
    handleSaveEdit,
    handleSync,
    handleSyncAll,
    handleTest,
    handleTestAll,
    historyDays,
    isConnecting,
    isLoading,
    isModalOpen,
    isSyncingAny,
    isTestingAny,
    latestSyncAt,
    pendingConnect,
    realAccounts,
    setBackfillNotice,
    setEditingAccount,
    setError,
    syncingAccounts,
    testingAccounts,
    todayIso,
    unreadableFrontiers,
  };
}
